import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faAward, faTrophy, faUserTie, faClipboard, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { Modal } from './Modal';
import './HelpModal.css';

export const ManagerHelpModal: React.FC<{ isOpen: boolean; onClose: () => void; }> = ({ isOpen, onClose }) => (
    <Modal isOpen={isOpen} onClose={onClose} className="help-modal">
        <h2><FontAwesomeIcon icon={faBriefcase} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Manager Guide</h2>
        <p style={{ fontSize: '1.1em', marginBottom: '24px' }}>Watch your managerial legacy unfold! 🏆 Track your career progress, break records, and build a winning reputation across seasons. Every decision shapes your story.</p>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faUserTie} style={{ marginRight: '8px', color: 'var(--info-color)' }} />Manager Profile</h3>
            <p><strong>Your managerial career information:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Current Team:</strong> Which club you're managing</li>
                <li><strong>League:</strong> Which division you're competing in</li>
                <li><strong>Seasons Managed:</strong> Total number of seasons with current club</li>
                <li><strong>Win Rate:</strong> Percentage of matches won</li>
                <li><strong>Board Confidence:</strong> How happy your board is with your performance</li>
            </ul>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Low board confidence can lead to dismissal - keep winning!</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faClipboard} style={{ marginRight: '8px', color: 'var(--accent-text-color)' }} />Season Records</h3>
            <p><strong>Your performance across seasons:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Season:</strong> Which season (Season 1, 2, 3, etc.)</li>
                <li><strong>League Finish:</strong> Final position in the league</li>
                <li><strong>Points:</strong> Total league points earned (3 per win, 1 per draw)</li>
                <li><strong>W-D-L:</strong> Wins, draws, losses record</li>
                <li><strong>Promotions/Relegations:</strong> Movement between leagues</li>
            </ul>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faAward} style={{ marginRight: '8px', color: 'var(--warning-color)' }} />Achievements</h3>
            <p><strong>Major accomplishments:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>League Titles:</strong> Winning the league championship</li>
                <li><strong>Promotions:</strong> Getting promoted to a higher division</li>
                <li><strong>Cup Wins:</strong> Winning domestic or international cups</li>
                <li><strong>Record Streaks:</strong> Consecutive wins or unbeaten runs</li>
            </ul>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faTrophy} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Club Records</h3>
            <p><strong>Important statistics about your tenure:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li>Total matches managed</li>
                <li>All-time win percentage</li>
                <li>Biggest wins/losses</li>
                <li>Best and worst seasons</li>
            </ul>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Building a long-term winning record improves your reputation and unlocks better opportunities</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faUserTie} style={{ marginRight: '8px', color: 'var(--accent-text-color)' }} />Player Conversations</h3>
            <p><strong>Manage player morale and satisfaction:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Talk to Players:</strong> Initiate conversations once per season (cooldown after each)</li>
                <li><strong>Conversation Options:</strong> Praise, promise rewards, show faith, demand improvement, warn, dismiss concerns, or assert authority</li>
                <li><strong>Impact:</strong> Affects morale, contract length, performance, and loyalty</li>
                <li><strong>Player Requests:</strong> Players signal when they want to talk (💬 icon)</li>
            </ul>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Happy players perform better - manage morale carefully to keep your squad content</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faAward} style={{ marginRight: '8px', color: 'var(--info-color)' }} />Financial Incentives</h3>
            <p><strong>Boost player morale with rewards:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Wage Bonuses:</strong> Increase weekly salary to motivate players</li>
                <li><strong>Goal Bonuses:</strong> Offer rewards for match performance</li>
                <li><strong>Cost:</strong> Impacts your transfer budget and weekly finances</li>
                <li><strong>Effect:</strong> Improves morale and performance immediately</li>
            </ul>
            <p className="help-tip"><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} /><em>Use incentives strategically for key players or to prevent dissatisfaction</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '8px', color: 'var(--accent-text-color)' }} />Build Your Legacy</h3>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li>📈 <strong>Keep the Board Happy:</strong> Maintain high board confidence with consistent wins. Losing their faith = sacking.</li>
                <li>🎯 <strong>Set Realistic Goals:</strong> Don't expect a newly promoted team to win the league immediately. Build gradually.</li>
                <li>⚖️ <strong>Know When to Promote:</strong> Winning your league is great, but make sure your squad is ready for the next level!</li>
                <li>📊 <strong>Study Your History:</strong> Review past seasons to identify patterns and improve. Legends are built on consistency!</li>
                <li>⭐ <strong>Chase Prestige:</strong> Promotions boost your reputation and attract better players. Build upward!</li>
                <li>💰 <strong>Maximize Rewards:</strong> Winning titles and cups brings huge prize money - reinvest it wisely!</li>
            </ul>
        </div>

        <div className="modal-actions">
            <button className="button primary" onClick={onClose}>Got it!</button>
        </div>
    </Modal>
);
